import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FiberManualRecord, WarningAmber } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { Grid } from '@mui/system';
import { useParams } from 'react-router-dom';
import { captableAction } from 'common/actions';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  BREAKPOINT_ANALYSIS_TITLE,
  CAP_TABLE_SLUG,
  CAP_TABLE_TITLE,
  CASH_FLOW_LEDGER_SLUG,
  CASH_FLOW_LEDGER_TITLE,
  FUND_OWNERSHIP_SLUG,
  FUND_OWNERSHIP_TITLE,
} from 'common/constants/cap-table';
import { useCompanyBreadcrumb } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { CustomContainer, TabsNavigation } from 'components';
import ExcelExportContext from 'context/ExcelExportContext';
import LayoutContext from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { CapTableDialog, NewCompanyDialog } from 'layouts/Main/components';
import useCapTableVersionFilter from 'pages/CapTable/cap-table/utilities/useCapTableVersionFilter';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useCompanyMeasurementDateFilter } from 'services/hooks';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { getObjectValue } from 'utilities';
import BreakpointAnalysis from './breakpoint-analysis';
import CapTable from './cap-table';
import CashFlows from './cash-distributions';
import FundOwnership from './fund-ownership';

const CAP_TABLE_CONTAINER_CLASS = `${CAP_TABLE_SLUG}-container`;
const FUND_OWNERSHIP_CONTAINER_CLASS = `${FUND_OWNERSHIP_SLUG}-container`;
const BREAKPOINT_ANALYSIS_CONTAINER_CLASS = `${BREAKPOINT_ANALYSIS_SLUG}-container`;
const CASH_FLOW_LEDGER_CONTAINER_CLASS = `${CASH_FLOW_LEDGER_SLUG}-container`;

const CapTableView = () => {
  const { tableSlugParam } = useParams();
  const [{ captableInfo }, dispatch] = useStore();
  const { id: currentCapTableId } = getObjectValue(captableInfo);
  const { setCurrentSelectedValues } = useContext(ExcelExportContext);

  const { showPageForm, togglePageForm, isExportMode } = useContext(LayoutContext);
  const [showRelatedEntityDialog, setShowRelatedEntityDialog] = useState(false);
  const toggleRelatedEntityDialog = () => setShowRelatedEntityDialog(!showRelatedEntityDialog);

  // Track active tab for proper save button handling
  const [activeTabId, setActiveTabId] = useState<string>(tableSlugParam || CAP_TABLE_SLUG);

  // Update active tab when URL changes
  useEffect(() => {
    if (tableSlugParam) {
      setActiveTabId(tableSlugParam);
    }
  }, [tableSlugParam]);

  // Filters options
  const { MDDialogue, selectedMeasurementDate } = useCompanyMeasurementDateFilter();
  const {
    selectedVersion,
    onEditCaptable,
    updateCapTableVersions,
    saveAsNewProforma,
    versionName,
    onAddRelatedEntity,
    capTableVersionsByCompany,
    refreshCapTableVersions,
  } = useCapTableVersionFilter({
    selectedMeasurementDate,
    onAddRelatedEntity: toggleRelatedEntityDialog,
    togglePageForm,
  });

  const isBridgeSignalLoading = isExportMode && (!selectedMeasurementDate || !selectedVersion || !captableInfo?.id);
  useExportSignal({
    target: PdfExportTargets.CAP_TABLE.id,
    status: 'loading',
    enabled: isBridgeSignalLoading,
  });
  useExportSignal({
    target: PdfExportTargets.FUND_OWNERSHIP.id,
    status: 'loading',
    enabled: isBridgeSignalLoading,
  });
  useExportSignal({
    target: PdfExportTargets.BREAKPOINT_ANALYSIS.id,
    status: 'loading',
    enabled: isBridgeSignalLoading,
  });
  useExportSignal({
    target: PdfExportTargets.CASH_FLOW_LEDGER.id,
    status: 'loading',
    enabled: isBridgeSignalLoading,
  });

  const { allocationStatusBreadcrumb, reloadAllocationBreadcrumbInfo } = useGetAllocationStatusBreadcrumb();

  const handleActiveTabChange = useCallback(
    (tab: { id?: string }) => {
      if (tab?.id) {
        setActiveTabId(tab.id);
      }
    },
    []
  );

  useEffect(() => {
    setCurrentSelectedValues({
      cap_table: currentCapTableId,
      fo_cap_table: currentCapTableId,
      breakpoint_cap_table: currentCapTableId,
      waterfall_cap_table: currentCapTableId,
      cash_distributions_cap_table: currentCapTableId,
    });
  }, [currentCapTableId, setCurrentSelectedValues]);

  const activeTable = useMemo(() => {
    switch (activeTabId) {
      case CAP_TABLE_SLUG:
        return CAP_TABLE_TITLE;
      case FUND_OWNERSHIP_SLUG:
        return FUND_OWNERSHIP_TITLE;
      case BREAKPOINT_ANALYSIS_SLUG:
        return BREAKPOINT_ANALYSIS_TITLE;
      case CASH_FLOW_LEDGER_SLUG:
        return CASH_FLOW_LEDGER_TITLE;
      default:
        return '';
    }
  }, [activeTabId]);

  const { icon: status, title: label } = allocationStatusBreadcrumb;

  const captableBreadcrumb = useMemo(
    () => ({
      page: activeTable,
      item: versionName
        ? {
          label: versionName,
          allocationVersion: {
            label,
            status,
          },
        }
        : {},
    }),
    [activeTable, label, status, versionName]
  );

  useCompanyBreadcrumb(captableBreadcrumb);

  const breakpointLabelSuffix = useMemo(() => {
    if (captableInfo?.has_unconfigured_custom_breakpoints) {
      return (
        <Tooltip title="Some securities are missing values in the custom breakpoints configuration">
          <WarningAmber fontSize="small" color="warning" sx={{ ml: 0.5 }} />
        </Tooltip>
      );
    }
    if (captableInfo?.use_custom_breakpoints) {
      return (
        <Tooltip title="Custom breakpoints are active">
          <FiberManualRecord sx={{ ml: 0.5, fontSize: 15, color: 'success.main' }} />
        </Tooltip>
      );
    }
    return null;
  }, [captableInfo?.has_unconfigured_custom_breakpoints, captableInfo?.use_custom_breakpoints]);

  // Cap Table tabs configuration
  const capTableTabs = useMemo(
    () => [
      {
        id: CAP_TABLE_SLUG,
        label: CAP_TABLE_TITLE,
        content: (selectedVersion || !selectedMeasurementDate) && (
          <CustomContainer className={CAP_TABLE_CONTAINER_CLASS}>
            <Grid size={{ xs: 12 }}>
              <CapTable
                onEditCaptable={onEditCaptable}
                updateCapTableVersions={updateCapTableVersions}
                saveAsNewProforma={saveAsNewProforma}
                selectedMeasurementDate={selectedMeasurementDate}
                capTableId={currentCapTableId}
                isActiveTab={activeTabId === CAP_TABLE_SLUG}
              />
            </Grid>
          </CustomContainer>
        ),
      },
      {
        id: FUND_OWNERSHIP_SLUG,
        label: FUND_OWNERSHIP_TITLE,
        content: (selectedVersion || !selectedMeasurementDate) && (
          <CustomContainer className={FUND_OWNERSHIP_CONTAINER_CLASS} data-ref={PdfExportTargets.FUND_OWNERSHIP.id} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
            <Grid size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
              <FundOwnership
                updateCapTableVersions={updateCapTableVersions}
                saveAsNewProforma={saveAsNewProforma}
                selectedMeasurementDate={selectedMeasurementDate}
                reloadAllocationBreadcrumbInfo={reloadAllocationBreadcrumbInfo}
                capTableVersionsByCompany={capTableVersionsByCompany}
                isActiveTab={activeTabId === FUND_OWNERSHIP_SLUG}
              />
            </Grid>
          </CustomContainer>
        ),
      },
      {
        id: BREAKPOINT_ANALYSIS_SLUG,
        label: BREAKPOINT_ANALYSIS_TITLE,
        labelSuffix: breakpointLabelSuffix,
        content: (
          <CustomContainer className={BREAKPOINT_ANALYSIS_CONTAINER_CLASS}>
            <Grid size={{ xs: 12 }}>
              <BreakpointAnalysis
                updateCapTableVersions={updateCapTableVersions}
                saveAsNewProforma={saveAsNewProforma}
                selectedMeasurementDate={selectedMeasurementDate}
                isActiveTab={activeTabId === BREAKPOINT_ANALYSIS_SLUG}
              />
            </Grid>
          </CustomContainer>
        ),
      },
      {
        id: CASH_FLOW_LEDGER_SLUG,
        label: CASH_FLOW_LEDGER_TITLE,
        content: (
          <CustomContainer className={CASH_FLOW_LEDGER_CONTAINER_CLASS} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
            <Grid size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}} data-ref={PdfExportTargets.CASH_FLOW_LEDGER.id}>
              <CashFlows
                updateCapTableVersions={updateCapTableVersions}
                saveAsNewProforma={saveAsNewProforma}
                selectedMeasurementDate={selectedMeasurementDate}
                reloadAllocationBreadcrumbInfo={reloadAllocationBreadcrumbInfo}
                capTableVersionsByCompany={capTableVersionsByCompany}
                isActiveTab={activeTabId === CASH_FLOW_LEDGER_SLUG}
              />
            </Grid>
          </CustomContainer>
        ),
      }
    ],
    [
      selectedVersion,
      selectedMeasurementDate,
      onEditCaptable,
      updateCapTableVersions,
      saveAsNewProforma,
      activeTabId,
      currentCapTableId,
      reloadAllocationBreadcrumbInfo,
      capTableVersionsByCompany,
      isExportMode,
      breakpointLabelSuffix,
    ]
  );

  // Clean cap table info in the global store when leaving the page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => dispatch(captableAction.setCaptableInfo({})), []);

  return (
    <>
      <Grid marginY={4}>
        <TabsNavigation
          mountOnActive
          tabs={capTableTabs}
          onActiveTabChange={handleActiveTabChange}
          updateUrlOnTabChange
        />
      </Grid>
      <NewCompanyDialog
        open={showRelatedEntityDialog}
        onClose={toggleRelatedEntityDialog}
        onSave={refreshCapTableVersions}
        isRelatedEntity
      />

      {captableInfo && <CapTableDialog open={showPageForm} onClose={togglePageForm} onSave={onEditCaptable} />}
      <MDDialogue />
    </>
  );
};

export default CapTableView;

export { CapTableView, CapTable, FundOwnership, BreakpointAnalysis, CashFlows };
